import type {
  Category,
  ConsentKitConfig,
  ConsentRecord,
  ConsentState,
  Decision,
  LocaleCode,
  ResolvedRegion,
} from "../types";
import { deniedDecision, grantedDelta, revokedDelta } from "./categories";
import { createRecord, uploadRecord } from "./consent-record";
import { clearRecord, loadRecord, saveRecord } from "./storage";

export interface DecisionChange {
  previous: Decision;
  next: Decision;
  granted: Category[];
  revoked: Category[];
  record: ConsentRecord;
}

/**
 * Holds the current decision and is the only writer of persisted consent.
 *
 * Starts from `baseline`, which the caller derives from the regime: fully denied under the
 * DSGVO, because nothing may be treated as consented until a stored record is found or the
 * user acts (§ 25 Abs. 1 TDDDG); the enabled set under US law, where processing is lawful
 * until the consumer objects. The store itself takes no view — it is handed the starting
 * point so there is exactly one place in the library that decides what "undecided" means.
 */
export class ConsentStore {
  private decision: Decision;
  private record: ConsentRecord | null = null;

  constructor(
    private readonly config: ConsentKitConfig,
    private readonly locale: LocaleCode,
    private readonly region: ResolvedRegion,
    private readonly baseline: Decision,
  ) {
    this.decision = { ...baseline };
  }

  /** Reads a still-valid stored record, if any. Returns true when the banner can stay hidden. */
  restore(): boolean {
    const stored = loadRecord(this.config);
    if (!stored) return false;
    // A decision taken under the other regime is not portable: an EU "reject all" is not a
    // US opt-out, and a US default is not consent. Fall back to the baseline and ask again
    // rather than reinterpret what the visitor meant.
    if (stored.regime !== this.region.regime) return false;
    this.record = stored;
    this.decision = { ...deniedDecision(), ...stored.decision, necessary: true };
    return true;
  }

  get state(): ConsentState {
    return { decision: { ...this.decision }, decided: this.record !== null, record: this.record };
  }

  has(category: Category): boolean {
    return this.decision[category] === true;
  }

  /** Records a deliberate user decision, persists it, and reports the delta. */
  decide(next: Decision, method: ConsentRecord["method"]): DecisionChange {
    const previous = this.decision;
    const normalized: Decision = { ...next, necessary: true };
    const record = createRecord(this.config, normalized, method, this.locale, this.region);

    this.decision = normalized;
    this.record = record;
    saveRecord(this.config, record);
    uploadRecord(this.config, record);

    return {
      previous,
      next: normalized,
      granted: grantedDelta(previous, normalized),
      revoked: revokedDelta(previous, normalized),
      record,
    };
  }

  /** Wipes the decision so the banner is shown again — used by the public `reset()`. */
  reset(): void {
    clearRecord(this.config);
    this.decision = { ...this.baseline };
    this.record = null;
  }
}
